import type { AHTWeightTreatmentPlan, AHTEpisodeOrigin, AHTDrugId } from "@/lib/ahtTreatmentPlanData";
import type { TreatmentPlanDates } from "@/lib/pharmacyFirst/utils/calculateTreatmentPlanDates";
import type { AHTPlannedAppointment } from "@/lib/pharmacyFirst/utils/generateAHTAppointments";

export interface BuildAHTTreatmentPlanInput {
  patientId: string;
  patientName: string;
  consultationId: string;
  pharmacistName: string;
  pharmacistRegistrationNumber: string;
  createdBy: string;
  episodeOrigin: AHTEpisodeOrigin;
  resolvedDrugId: AHTDrugId;
  pgdId: string;
  pgdVersion: string;
  initialDose: string;
  baselineWeightKg: number;
  baselineWaistCircumferenceCm: number | null;
  baselineBmi: number;
  dates: TreatmentPlanDates;
  batchNumber: string;
  expiryDate: string;
  brandName: string | null;
  clinicalRationale: string;
  plannedAppointments: AHTPlannedAppointment[];
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function buildAHTTreatmentPlan(
  input: BuildAHTTreatmentPlanInput,
): AHTWeightTreatmentPlan {
  const {
    patientId,
    patientName,
    consultationId,
    pharmacistName,
    pharmacistRegistrationNumber,
    createdBy,
    episodeOrigin,
    resolvedDrugId,
    pgdId,
    pgdVersion,
    initialDose,
    baselineWeightKg,
    baselineWaistCircumferenceCm,
    baselineBmi,
    dates,
    batchNumber,
    expiryDate,
    brandName,
    clinicalRationale,
    plannedAppointments,
  } = input;

  const description =
    "AHT weight management treatment plan — " +
    (episodeOrigin === "initialSupply" ? "initial supply" : "transfer in");

  return {
    id: crypto.randomUUID(),
    name: `${capitalise(resolvedDrugId)} weight management — ${patientName}`,
    description,
    status: "Active",
    startDate: dates.supplyDate,
    patientId,
    patientName,
    createdBy,
    createdAt: new Date().toISOString(),
    consultationId,
    goals: [],
    followUpAppointments: plannedAppointments.map((a) => a.id),
    linkedPrescriptions: [],

    plannedAppointmentDetails: plannedAppointments,
    episodeOrigin,
    resolvedDrugId,
    pgdIdAtInitiation: pgdId,
    pgdVersionAtInitiation: pgdVersion,
    parentConsultationId: consultationId,
    baselineWeightKg,
    baselineWaistCircumferenceCm,
    baselineBmi,
    currentDose: initialDose,
    currentWeek: 1,
    maintenanceEpisodeCount: 0,
    initialSupplyDate: dates.supplyDate,
    nextQuestionnaireDueDate: dates.nextQuestionnaireDueDate,
    nextSupplyDueDate: dates.nextSupplyDueDate,
    nextDrugSpecificReviewDueDate: dates.nextDrugSpecificReviewDueDate,
    drugSpecificReviewLabel: dates.drugSpecificReviewLabel,
    nextSectionBReviewDueDate: dates.nextSectionBReviewDueDate,
    questionnaireStatus: "not_sent",
    questionnaireSentAt: null,
    questionnaireReturnedAt: null,

    supplyHistory: [
      {
        id: crypto.randomUUID(),
        consultationId,
        supplyDate: dates.supplyDate,
        drugId: resolvedDrugId,
        dose: initialDose,
        batchNumber,
        expiryDate,
        brandName,
        weightKg: baselineWeightKg,
        waistCircumferenceCm: baselineWaistCircumferenceCm ?? undefined,
        weekNumber: 1,
        wasInTitrationPhase: dates.isInTitrationPhase,
        pharmacistName,
        pharmacistRegistrationNumber,
        clinicalRationale,
        prescriberDecision: "continue",
      },
    ],
  };
}
